import {useState, useCallback, useMemo, useEffect} from 'react';
import {observer} from 'mobx-react';
import _ from 'lodash';
import {browserHistory} from 'react-router';
import InteractiveContext from 'main/components/tables/interactive/components/InteractiveContext';
import Pagination from 'main/components/tables/interactive/components/pagination/Pagination';
import {FilterRow} from 'main/components/tables/interactive/components/FilterRow';
import AccordionTableContent from 'main/components/tables/interactive/components/accordion_table/AccordionTableContent';
import Panel from 'shared/components/panels/Panel';
import Header from 'main/components/tables/interactive/components/header/Header';
import {DomainStore} from 'shared/store';
import loadStateFromUrl from 'main/components/tables/interactive/state/loadStateFromUrl';
import SelectionState from 'main/components/tables/interactive/state/SelectionState';

const TableWrapper = observer(({wrapInPanel, children}) => {
  if (wrapInPanel) {
    return (
      <Panel>{children}</Panel>
    );
  }

  return (
    <div>{children}</div>
  );
});

const InteractiveAccordionTable = observer(function InteractiveAccordionTable({
  searchable = true,
  paginatable = true,
  proxy,
  folderProxy,
  saveStateToUrl,
  onMount,
  onRefresh,
  children,
  empty,
  initialFilter,
  initialSorting,
  hidePaginationInfo = false,
  fancyFiltersEnabled,
  onFilterUpdated = () => {},
  scopeOptions,
  columns = [],
  showLinks = true,
  localization = {},
  wrapInPanel = true,
  title = '',
  description,
  showAddButton = true,
  onAdd = () => {},
  tableActionButtons,
  horizontalScroll = false,
  selectable = false,
  onSelectionChange,
  showHeaders = true,
  nestedRows,
  onExpand,
  ...rest
}) {
  const [isFoldersUpdating, setIsFoldersUpdating] = useState(true);
  const [folders, setFolders] = useState([]);
  const [currentPage, setCurrentPage] = useState(new URLSearchParams(location.search).get('p') || 1);
  const [filter, setFilter] = useState({});
  const [isEmpty, setIsEmpty] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const initialExpandedIds = useMemo(() => {
    if (!saveStateToUrl) return [];

    const state = loadStateFromUrl();
    return state.expandedIds || [];
  }, []);

  const [expandedFolderIds, setExpandedFolderIds] = useState(initialExpandedIds);

  const selectionState = useMemo(() => {
    if (!selectable) return null;

    const nestedRowsAttr = nestedRows && nestedRows.length > 0 ? nestedRows[0].attribute : null;

    return new SelectionState({
      onSelectionChange,
      nestedRowsAttr,
      onExpand
    });
  }, []);

  useEffect(() => {
    if (!selectable || !selectionState) return;

    selectionState.setFilter(filter);
  }, [filter]);

  const updateExpandedIds = useCallback((newExpandedIds) => {
    setExpandedFolderIds(newExpandedIds);

    if (!saveStateToUrl) return;

    const location = browserHistory.getCurrentLocation();
    const query = {...location.query};

    if (newExpandedIds.length > 0) {
      query.ex = newExpandedIds.join(',');
    } else {
      delete query.ex;
    }

    browserHistory.replace({...location, query});
  }, [saveStateToUrl]);

  const handleFolderExpanded = useCallback((folderId) => {
    const newExpandedIds = [...expandedFolderIds, folderId];
    updateExpandedIds(newExpandedIds);
  }, [expandedFolderIds, updateExpandedIds]);

  const handleFolderCollapsed = useCallback((folderId) => {
    const newExpandedIds = expandedFolderIds.filter(id => id !== folderId);
    updateExpandedIds(newExpandedIds);
  }, [expandedFolderIds, updateExpandedIds]);

  const fetchFolders = useCallback(async (filterObj) => {
    if (!folderProxy) return;

    const {endpoint, model, modelType} = folderProxy;
    const store = new DomainStore();
    const url = typeof endpoint === 'function' ? endpoint(filterObj) : endpoint;
    await store._compose([url]);
    setFolders(store._getAll(modelType, model));
  }, [folderProxy]);

  const handleOnMount = useCallback(async (agent) => {
    setIsEmpty(agent.isEmpty);

    await fetchFolders(agent.filter.toJSON());

    onMount && onMount(
      {
        ...agent,
        refreshFolders: () => fetchFolders(agent.filter.toJSON())
      }
    );
    setIsFoldersUpdating(false);
    setIsLoading(false);
  }, [onMount, fetchFolders]);

  const handleFilterUpdated = useCallback(async (filterValue) => {
    setIsFoldersUpdating(true);
    setCurrentPage(1);
    setFilter(filterValue.toJSON());
    onFilterUpdated(filterValue);
    await fetchFolders(filterValue.toJSON());
    setIsFoldersUpdating(false);
  }, [onFilterUpdated, fetchFolders]);

  const handlePaginationUpdated = useCallback(({currentPage: newPage}) => {
    setCurrentPage(newPage);
  }, []);

  const {emptyStateMessage} = localization;

  if (!horizontalScroll) {
    const selectionAdjustment = selectable ? 1 : 0;
    if (showLinks && _.sumBy(columns, 'width') !== 11 - selectionAdjustment) {
      throw new Error(`Columns must add up to a width of ${11 - selectionAdjustment} when showLinks is set to true${selectable ? ' and selectable is set to true' : ''}. (1 column is taken by the dropdown with edit/delete links.)`);
    }
    if (!showLinks && _.sumBy(columns, 'width') !== 12 - selectionAdjustment) {
      throw new Error(`Columns must add up to a width of ${12 - selectionAdjustment}${selectable ? ' when selectable is set to true' : ''}.`);
    }
  }

  if (isEmpty && _.isFunction(empty)) {
    const EmptyState = empty;
    return <EmptyState {...rest} />;
  }

  const foldersToShow = currentPage === 1 && !filter.search ? folders : [];

  return (
    <TableWrapper wrapInPanel={wrapInPanel}>
      <Header
        showAddButton={showAddButton}
        onAdd={onAdd}
        actionButtons={tableActionButtons}
        localization={_.get(localization, 'interactive')}
        description={description}
        title={title}
        loading={isLoading}
      />
      <InteractiveContext
        proxy={proxy}
        onMount={handleOnMount}
        onRefresh={onRefresh}
        saveStateToUrl={saveStateToUrl}
        empty={empty}
        initialFilter={initialFilter}
        initialSorting={initialSorting}
        onFilterUpdated={handleFilterUpdated}
        onPaginationUpdated={handlePaginationUpdated}
      >
        <FilterRow className='AccordionTableFilters' style={showHeaders ? {} : {margin: 0}} searchable={searchable} fancyFiltersEnabled={fancyFiltersEnabled} scopeOptions={scopeOptions}>
          {children}
        </FilterRow>
        <AccordionTableContent
          columns={columns}
          emptyStateMessage={emptyStateMessage}
          showLinks={showLinks}
          localization={localization}
          folders={foldersToShow}
          isFoldersUpdating={isFoldersUpdating}
          horizontalScroll={horizontalScroll}
          expandedFolderIds={expandedFolderIds}
          onFolderExpanded={handleFolderExpanded}
          onFolderCollapsed={handleFolderCollapsed}
          selectable={selectable}
          selectionState={selectionState}
          showHeaders={showHeaders}
          nestedRows={nestedRows}
          onExpand={onExpand}
          {...rest}
        />
        {paginatable && <Pagination hidePaginationInfo={hidePaginationInfo} />}
      </InteractiveContext>
    </TableWrapper>
  );
});

export default InteractiveAccordionTable;
